import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { Search, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import type { RouterOutput } from "@lattice/backend/trpc";
import { getStockLogoUrl } from "@lattice/core-lib/utils/stock-logo";
import { IconButton } from "@lattice/frontend/components/kit/IconButton";
import { TextField } from "@lattice/frontend/components/kit/TextField";
import { cn } from "@lattice/frontend/lib/utils/ui";
import styles from "./StockSearchView.module.css";

export type T_StockSearchItem = RouterOutput["stock"]["search_stock"][number];

export type T_AutocompleteSelectedItem = {
  image: string | null;
  label: string;
  value: string;
};

export type T_StockSearchViewProps = {
  appearance?: "default" | "fill";
  className?: string;
  fadingStocks?: Set<string>;
  inlineOptions?: boolean;
  isLoading?: boolean;
  items: readonly T_StockSearchItem[];
  onClearSelectedItems?: () => void;
  onFocus?: () => void;
  onSearchChange: (value: string) => void;
  onSelect: (stock: T_StockSearchItem) => void;
  optionEndIcon?: React.ReactNode | ((symbol: string) => React.ReactNode);
  placeholder?: string;
  searchValue: string;
  selectedItems?: readonly T_AutocompleteSelectedItem[];
  selectionMode?: "multiple" | "single";
};

function StockLogo({
  image,
  size = "option",
  symbol,
}: {
  image: string | null;
  size?: "option" | "pill";
  symbol: string;
}) {
  const source = image
    ? image.startsWith("http") || image.startsWith("data:")
      ? image
      : `data:image/png;base64,${image}`
    : getStockLogoUrl(symbol);
  return (
    <img
      alt=""
      className={cn(styles.logo, size === "pill" ? styles.logoPill : "")}
      onError={(event) => {
        const fallback = getStockLogoUrl(symbol);
        if (event.currentTarget.src !== fallback) {
          event.currentTarget.src = fallback;
        } else {
          event.currentTarget.hidden = true;
        }
      }}
      src={source}
    />
  );
}

export function StockSearchView({
  appearance = "default",
  className,
  fadingStocks,
  isLoading = false,
  items,
  onClearSelectedItems,
  onFocus,
  onSearchChange,
  onSelect,
  optionEndIcon,
  placeholder = "Search stocks…",
  searchValue,
  selectedItems = [],
  selectionMode = "single",
}: T_StockSearchViewProps) {
  const [width, setWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const hasSelectedItems = selectedItems.length > 0;
  const locksSingleSelection = selectionMode === "single" && hasSelectedItems;
  const canClear =
    searchValue.trim().length > 0 ||
    (hasSelectedItems && onClearSelectedItems !== undefined);

  // eslint-disable-next-line no-restricted-syntax -- ResizeObserver synchronizes external element geometry.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={cn(
        styles.autocomplete,
        hasSelectedItems ? styles.hasSelections : "",
        className,
      )}
      data-appearance={appearance}
      ref={containerRef}
    >
      <Combobox
        as="div"
        onChange={(symbol: string | null) => {
          const stock = items.find((item) => item.symbol === symbol);
          if (stock) onSelect(stock);
        }}
        onFocus={onFocus}
      >
        {({ open }) => (
          <>
            <TextField
              ariaLabel={placeholder}
              className={cn(
                open && searchValue.trim().length > 0 ? styles.fieldOpen : "",
              )}
              containerStyle={{ width: "100%" }}
              leading={
                hasSelectedItems ? (
                  <span className={styles.selectedItems}>
                    {selectedItems.map((item) => (
                      <span className={styles.selectedPill} key={item.value}>
                        <StockLogo
                          image={item.image}
                          size="pill"
                          symbol={item.value}
                        />
                        <span>{item.label}</span>
                      </span>
                    ))}
                  </span>
                ) : (
                  <Search aria-hidden className={styles.searchIcon} />
                )
              }
              placeholder={locksSingleSelection ? undefined : placeholder}
              renderInput={(input) => (
                <ComboboxInput
                  aria-describedby={input.ariaDescribedBy}
                  aria-invalid={input.ariaInvalid}
                  aria-label={input.ariaLabel}
                  autoComplete={input.autoComplete}
                  autoFocus={input.autoFocus}
                  className={cn(
                    input.className,
                    locksSingleSelection ? styles.inputSelected : "",
                  )}
                  disabled={input.disabled}
                  id={input.id}
                  name={input.name}
                  onChange={(event) => onSearchChange(event.target.value)}
                  placeholder={input.placeholder}
                  readOnly={locksSingleSelection}
                  type={input.type}
                  value={searchValue}
                />
              )}
              trailing={
                canClear ? (
                  <span className={styles.trailing}>
                    <IconButton
                      ariaLabel={
                        searchValue.trim().length > 0
                          ? "Clear search"
                          : "Clear selection"
                      }
                      icon={<X className={styles.clearIcon} />}
                      onClick={() => {
                        if (searchValue.trim().length > 0) {
                          onSearchChange("");
                          return;
                        }
                        onClearSelectedItems?.();
                      }}
                      size="compact"
                      variant="quiet"
                    />
                  </span>
                ) : null
              }
              type="text"
              value={searchValue}
            />
            {searchValue && open ? (
              <ComboboxOptions
                className={styles.options}
                modal={false}
                portal={false}
                style={{ width }}
              >
                {isLoading && items.length === 0 ? (
                  <div className={styles.status}>Searching…</div>
                ) : null}
                {!isLoading && items.length === 0 ? (
                  <div className={styles.status}>No results</div>
                ) : null}
                {!isLoading
                  ? items.map((stock) => (
                      <ComboboxOption
                        className={cn(
                          styles.option,
                          fadingStocks?.has(stock.symbol)
                            ? styles.optionFading
                            : "",
                        )}
                        key={stock.symbol}
                        value={stock.symbol}
                      >
                        <StockLogo image={stock.image} symbol={stock.symbol} />
                        <span className={styles.copy}>
                          <span className={styles.symbol}>{stock.symbol}</span>
                          <span className={styles.name}>{stock.name}</span>
                        </span>
                        {typeof optionEndIcon === "function"
                          ? optionEndIcon(stock.symbol)
                          : optionEndIcon}
                      </ComboboxOption>
                    ))
                  : null}
              </ComboboxOptions>
            ) : null}
          </>
        )}
      </Combobox>
    </div>
  );
}
